/* eslint-disable react/display-name */
import { useEffect, useState } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, Datepicker, HookFormResolver, Text } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { Alert, ButtonGroup } from 'react-bootstrap';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { DateRequired, ObjectRequired } from '../../../../core/validations/common';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import ProgressBar from '../components/progress-bar';
import { useGetReportByOperations, useGetReportPeriodicities } from '../hooks';
import { label, labelVector } from '../translation';
import { type Report, type ReportRow, type TaxRow } from '../types';

import { type ProgressStepProps } from './types';

interface Props {
  reports: Array<Report>;
  onSubmit: (data: any) => void;
  data: Array<TaxRow>;
  readonly?: boolean;
}

interface RowProps {
  idName: string;
  nameReport: string;
  expirationName: string;
  periodicityName: string;
  startName: string;
  data: ReportRow;
  errors: any;
  readonly?: boolean;
}

const validations = yup.object().shape({
  vector: yup.array().of(
    yup.object().shape({
      periodicity: ObjectRequired,
      start: DateRequired
    })
  )
});

const RowVectorReports = ({
  data,
  idName,
  nameReport: nameReport,
  expirationName,
  periodicityName,
  startName,
  readonly,
  errors
}: RowProps) => {
  const { data: periodicities, loading, error } = useGetReportPeriodicities(data.id, true, true);

  const err = errors ? errors['periodicity'] : undefined;
  const { setValue, register } = useFormContext();

  if (loading || error) {
    return <></>;
  }

  return (
    <Row>
      <Col>
        <input defaultValue={data.id} hidden={true} {...register(idName)} />
        <Text
          label=""
          name={nameReport}
          readonly={true}
          defaultValue={`${data.code} - ${data.shortName}`}
        />
      </Col>
      <Col>
        <Combobox
          defaultValue={data.periodicity}
          customError={err}
          label=""
          name={periodicityName}
          display="name"
          code="id"
          data={periodicities}
          readOnly={readonly}
          whenChange={(item: any) => {
            if (item) {
              setValue(expirationName, item.filingDueDay);
            }
          }}
        />
      </Col>
      <Col>
        <Text defaultValue={data.expiration} label="" name={expirationName} readonly={true} />
      </Col>

      <Col>
        <Datepicker readOnly={readonly} customError={err} label="" name={startName} />
      </Col>
    </Row>
  );
};

export const ReportVector = ({ onSubmit, reports: reportsByOperations, data, readonly }: Props) => {
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState<Array<ReportRow>>([]);
  const [init, setInit] = useState<boolean>(false);

  useEffect(() => {
    const newData = [] as Array<ReportRow>;

    reportsByOperations.forEach((x) => {
      const newValue = data?.find((y) => y.id === x.id);
      if (newValue) {
        newData.push({ ...newValue, shortName: x.shortName, code: x.code });
      } else {
        newData.push({ id: x.id, shortName: x.shortName, code: x.code } as ReportRow);
      }
    });
    setReports(newData ?? []);
    setInit(true);
  }, []);

  if (!init) {
    return <></>;
  }

  return (
    <>
      <HookFormResolver
        initialValues={{ vector: reports }}
        nameForm="formReportVector"
        onSubmit={(c: any) => onSubmit(c)}
        validations={validations}
      >
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
            control,
            name: 'vector',
            keyName: 'fieldId'
          });
          const hasData = fields?.length > 0;
          return (
            <>
              {hasData && (
                <>
                  <hr />
                  <Row>
                    <Col>{label(t, 'report')}</Col>
                    <Col>{labelVector(t, 'periodicity')}</Col>
                    <Col>{labelVector(t, 'expiration')}</Col>
                    <Col>{labelVector(t, 'startOperation')}</Col>
                  </Row>
                  {fields.map((obj: any, index: number) => {
                    const err = errors?.vector ? (errors?.vector as any)[index] : undefined;

                    return (
                      <RowVectorReports
                        errors={err}
                        data={obj}
                        key={obj.fieldId}
                        idName={`vector[${index}].id`}
                        nameReport={`vector[${index}].report`}
                        expirationName={`vector[${index}].expiration`}
                        periodicityName={`vector[${index}].periodicity`}
                        startName={`vector[${index}].start`}
                        readonly={readonly}
                      />
                    );
                  })}
                </>
              )}

              {!hasData && (
                <>
                  <hr />
                  <Row>
                    <Col>
                      <Alert variant="warning">
                        {t('taxpayer-request.messages.withoutOperationsReport')}
                      </Alert>
                    </Col>
                  </Row>
                </>
              )}
            </>
          );
        }}
      </HookFormResolver>
    </>
  );
};

export default (props: ProgressStepProps) => {
  const operationsIds = useTaxpayerRequestStore(
    (s) => s.form.operations?.map((x) => x.id) as Array<string>
  );
  const taxSelected = useTaxpayerRequestStore((s) => s.form.reports);
  const addReportPeriodicities = useTaxpayerRequestStore((s) => s.addReportPeriodicities);
  const [{ data, loading, error }] = useGetReportByOperations(
    operationsIds,
    operationsIds?.length > 0
  );
  const onSubmit = (data: any) => {
    addReportPeriodicities(data.vector);
    props.nextStep();
  };
  if (loading || error) {
    return <></>;
  }
  return (
    <>
      <ReportVector
        reports={data || []}
        onSubmit={onSubmit}
        data={taxSelected ? taxSelected : []}
      />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button action={ButtonAction.previous} onClick={() => props.previousStep()} />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            variant="primary"
            form="formReportVector"
            action={ButtonAction.next}
            type="submit"
          />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
